<template>
  <div class="page">
    <header class="page-header">
      <div class="page-header-bar">绑定终端</div>
      <div class="page-header-content">
        <h1>第一步：扫描区域码</h1>
        <p>请将摄像头对准区域码</p>
      </div>
    </header>

    <main class="page-body">
      <div class="page-body-scroll">
        <Html5QrcodeScanner ref="scannerRef" mode="qr" :qrboxSize="220" @success="handleSuccess" @error="handleError" />

        <div class="status-card">
          <p class="status-text">{{ statusText }}</p>

          <div class="info-card">
            <div class="info-label">所属区域：</div>
            <div class="info-value">{{ areaName }}</div>
          </div>

          <div v-if="hasScanResult" class="status-actions">
            <van-button plain size="small" type="primary" @click="handleRescan">重新扫码</van-button>
          </div>
        </div>
      </div>
    </main>

    <footer class="page-footer safe-bottom">
      <van-button block type="primary" :disabled="!areaName" @click="goNext">区域确认</van-button>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import { resolveRegionQrCode } from '@/api/install'
import Html5QrcodeScanner from '@/components/install/Html5QrcodeScanner.vue'
import { useMainStore } from '@/store'

const scannerRef = ref<InstanceType<typeof Html5QrcodeScanner> | null>(null)
const router = useRouter()
const store = useMainStore()
const statusText = ref('')
const areaName = ref('')
const hasScanResult = ref(false)
const resolving = ref(false)

const extractToken = (value: string) => {
  const parsed = new URLSearchParams(value)
  return parsed.get('token') || value.trim()
}

const handleSuccess = async (value: string) => {
  if (resolving.value) {
    return
  }

  const token = extractToken(value)
  if (!token) {
    hasScanResult.value = true
    statusText.value = '区域码无效，请重新扫码'
    return
  }

  resolving.value = true
  statusText.value = '区域码识别成功，正在加载区域信息'
  try {
    const resp = await resolveRegionQrCode(token)
    const installContext = resp.data
    store.setInstallContext({
      ...installContext,
      token
    })
    store.setInstallSelection({
      terminalId: null,
      terminalCode: '',
      buildingId: null,
      buildingName: '',
      shaftId: null,
      shaftName: '',
      deviceType: '智能屏',
      installPosition: '轿厢内'
    })
    hasScanResult.value = true
    areaName.value = installContext.regionName
    statusText.value = '区域码识别成功'
    showToast({ type: 'success', message: '区域码识别成功' })
  } catch (error) {
    store.clearInstallContext()
    hasScanResult.value = true
    areaName.value = ''
    statusText.value = error instanceof Error ? error.message : '区域码识别失败，重新识别'
    showToast(statusText.value)
  } finally {
    resolving.value = false
  }
}

const handleError = () => {
  statusText.value = '区域码识别失败，重新识别'
}

const handleRescan = async () => {
  store.clearInstallContext()
  hasScanResult.value = false
  areaName.value = ''
  statusText.value = ''
  await scannerRef.value?.restart()
}

const goNext = () => {
  router.push('/install/location')
}
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  overflow: hidden;
  box-sizing: border-box;
  background: #f7f8fa;
  padding-top: calc(env(safe-area-inset-top) + 48px);
}

.page-header {
  flex: none;
  text-align: center;
}

.page-header-bar {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(env(safe-area-inset-top) + 48px);
  padding-top: env(safe-area-inset-top);
  box-sizing: border-box;
  background: #3e87f8;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
}

.page-header-content {
  padding: 16px 0 8px;
}

.page-header h1 {
  margin: 0;
  font-size: 22px;
  line-height: 1.4;
  color: #1f2937;
}

.page-header p {
  margin: 8px 0 0;
  font-size: 14px;
  color: #6b7280;
}

.page-body {
  flex: 1;
  min-height: 0;
  margin: 8px 10px 0;
  padding: 16px;
  overflow: hidden;
  box-sizing: border-box;
  border-radius: 16px;
  background: #eef0f4;
}

.page-body-scroll {
  display: grid;
  gap: 16px;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.page-body-scroll::-webkit-scrollbar {
  display: none;
}

.status-text {
  display: block;
  margin: 0 0 12px;
  font-size: 14px;
  text-align: center;
  color: #374151;
}

.status-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.status-actions :deep(.van-button) {
  border-color: #3e87f8;
  border-radius: 999px;
}

.info-card {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  border-radius: 8px;
  background: #fff;
}

.info-label {
  flex: none;
  font-size: 12px;
  color: #9ca3af;
}

.info-value {
  min-width: 0;
  flex: 1;
  font-size: 16px;
  text-align: right;
  word-break: break-all;
  color: #111827;
}

.page-footer {
  flex: none;
  padding: 8px 16px 0;
}

.safe-bottom {
  padding-bottom: calc(env(safe-area-inset-bottom) + 16px);
}
</style>
